/**
 * =============================================================================
 * Hook: useContractAssetUrl
 * Versão: 1.0.0 (2026-05-01)
 *
 * Descrição:
 *   Resolve um caminho de asset de contrato (capa, plano de fundo, PDF base)
 *   para uma URL utilizável no browser:
 *     - Se já é http(s), usa diretamente.
 *     - Se é caminho relativo (legado do bucket `contratos`), gera signed URL.
 *
 *   Cacheável e seguro contra race-condition (cancela updates após unmount).
 * =============================================================================
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { resolveContractPdfBucket } from '@/lib/contracts/pdfBucketResolver';

const log = createLogger('useContractAssetUrl');

export function useContractAssetUrl(path: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(() => (path?.startsWith('http') ? path : null));

  useEffect(() => {
    let cancelled = false;

    if (!path) {
      setUrl(null);
      return;
    }

    // Caso 1: URL pública/galeria — usa direto
    if (path.startsWith('http')) {
      setUrl(path);
      return;
    }

    // Caso 2: caminho relativo — gera signed URL (1h) no bucket correto
    //   (contratos p/ gerados pelo backend, anexos p/ uploads do wizard)
    const bucket = resolveContractPdfBucket(path);
    supabase.storage
      .from(bucket)
      .createSignedUrl(path, 3600)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          log.error('Falha ao gerar signed URL', { path, error: error.message });
          setUrl(null);
          return;
        }
        if (data?.signedUrl) setUrl(data.signedUrl);
      });

    return () => {
      cancelled = true;
    };
  }, [path]);

  return url;
}
